/** Virtual wallet gateways — amounts are stored in USD (matches API). */
export const FIAT_GATEWAYS = [
  'Overall Asset',
  'Funding Wallet',
  'Invested Capital',
  'Accumulated Profit',
  'Referral Wallet',
  'Withdrawable Balance',
];

export const STABLECOIN_SYMBOLS = ['USDT', 'USDTERC20', 'USDC', 'BUSD', 'DAI'];

export const formatUsd = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencySign: 'standard',
  }).format(amount || 0);

export const isFiatGateway = (gateway) =>
  FIAT_GATEWAYS.includes(gateway);

/** Payment wallet row from wallet_addresses (deposit / receive flows). */
export const isBankWallet = (row) =>
  String(row?.wallet_type || '').trim().toLowerCase() === 'bank';

export const isCryptoWallet = (row) => {
  if (!row || isBankWallet(row)) return false;
  const type = String(row.wallet_type || '').trim().toLowerCase();
  if (type === 'crypto') return true;
  return Boolean(row.coin_name && row.address);
};

export const isCryptoSymbol = (symbol, coinRates = []) =>
  coinRates.some(
    (c) => String(c.symbol || '').toUpperCase() === String(symbol || '').toUpperCase()
  ) && !isFiatGateway(symbol);

export const getPriceUsd = (symbol, coinRates = []) => {
  if (!symbol) return 0;
  const upper = String(symbol).toUpperCase();
  if (STABLECOIN_SYMBOLS.includes(upper)) return 1;
  const coin = coinRates.find(
    (c) => String(c.symbol || '').toUpperCase() === upper
  );
  if (!coin) return 0;
  const raw = coin.priceUsd ?? coin.price_usd;
  return parseFloat(raw) || 0;
};

/**
 * Native coin units for a symbol (aligned with API User::getCoinBalance).
 */
export const getCoinUnits = (
  symbol,
  { fundings = [], tradings = [], investments = [], withdrawals = [] } = {}
) => {
  let amount = 0;

  fundings.forEach((fund) => {
    if (fund.gateway === symbol && fund.status === 'successful') {
      amount += parseFloat(fund.amount) || 0;
    }
  });

  tradings.forEach((trade) => {
    if (trade.pair === symbol) {
      amount += parseFloat(trade.profit) || 0;
    }
  });

  tradings.forEach((trade) => {
    if (trade.pair === symbol && trade.status === 'open') {
      amount -= parseFloat(trade.amount) || 0;
    }
  });

  investments.forEach((investment) => {
    if (investment.payment_source === symbol && investment.status !== 'declined') {
      amount -= parseFloat(investment.amount_invested) || 0;
    }
  });

  withdrawals.forEach((withdrawal) => {
    if (withdrawal.source === symbol && withdrawal.status !== 'declined') {
      amount -= parseFloat(withdrawal.amount) || 0;
    }
  });

  return amount;
};

export const getCoinUsd = (symbol, units, coinRates = []) => {
  if (!units) return 0;
  if (!isCryptoSymbol(symbol, coinRates)) {
    return units;
  }
  return units * getPriceUsd(symbol, coinRates);
};

/**
 * Total wallet balance in USD — moves with live coin prices (matches API User::balance).
 */
export const computePortfolioBalanceUsd = ({
  fundings = [],
  tradings = [],
  investments = [],
  withdrawals = [],
  copyings = [],
  coinRates = [],
}) => {
  let balance = 0;

  coinRates.forEach((coin) => {
    const units = getCoinUnits(coin.symbol, {
      fundings,
      tradings,
      investments,
      withdrawals,
    });
    balance += getCoinUsd(coin.symbol, units, coinRates);
  });

  fundings.forEach((fund) => {
    if (
      isFiatGateway(fund.gateway) &&
      (fund.status === 'successful' || fund.status === 'approved')
    ) {
      balance += parseFloat(fund.amount) || 0;
    }
  });

  copyings.forEach((copier) => {
    if (
      copier.status !== 'declined' &&
      copier.status !== 'completed' &&
      copier.payment_source === 'Overall Asset'
    ) {
      balance -= parseFloat(copier.amount) || 0;
    }
  });

  copyings.forEach((copier) => {
    if (copier.status !== 'declined') {
      balance -= parseFloat(copier.profit) || 0;
    }
  });

  tradings.forEach((trade) => {
    if (trade.status === 'open') {
      balance -= parseFloat(trade.amount) || 0;
    }
  });

  tradings.forEach((trade) => {
    if (trade.status === 'closed') {
      balance += parseFloat(trade.profit) || 0;
    }
  });

  withdrawals.forEach((withdrawal) => {
    if (withdrawal.status !== 'declined') {
      balance -= parseFloat(withdrawal.amount) || 0;
    }
  });

  return balance;
};

/** Admin table: USD, coin units, rate, and notes for a funding row */
export const formatAdminFundingDetails = (funding, coinRates = []) => {
  const gateway = funding?.gateway;
  const status = (funding?.status || '').toLowerCase();
  const isCrypto = gateway && !isFiatGateway(gateway);

  const units = parseFloat(funding?.amount) || 0;
  const usdLocked =
    funding?.amount_usd != null && funding?.amount_usd !== ''
      ? parseFloat(funding.amount_usd)
      : null;
  const bookedRate =
    funding?.rate_usd_at_booking != null && funding?.rate_usd_at_booking !== ''
      ? parseFloat(funding.rate_usd_at_booking)
      : null;
  const livePrice = isCrypto ? getPriceUsd(gateway, coinRates) : 0;
  const liveUsd = isCrypto ? units * livePrice : units;

  if (!isCrypto) {
    return {
      usd: formatUsd(units),
      coinUnits: '—',
      rate: '—',
      liveUsd: '—',
      note: 'Fiat / wallet bucket',
    };
  }

  if (status === 'processing') {
    const usd = usdLocked ?? units;
    const estUnits = livePrice > 0 ? usd / livePrice : null;
    return {
      usd: formatUsd(usd),
      coinUnits: estUnits != null ? `~${formatCryptoAmount(estUnits)} ${gateway}` : '—',
      rate: livePrice > 0 ? `${formatUsd(livePrice)} / ${gateway}` : '—',
      liveUsd: '—',
      note: 'Pending approval (coin units set on approve)',
    };
  }

  if (bookedRate != null && bookedRate > 0) {
    const usd = usdLocked ?? units * bookedRate;
    return {
      usd: formatUsd(usd),
      coinUnits: `${formatCryptoAmount(units)} ${gateway}`,
      rate: `${formatUsd(bookedRate)} / ${gateway}`,
      liveUsd: formatUsd(liveUsd),
      note: usdLocked != null ? 'Booked at approval' : '',
    };
  }

  return {
    usd: usdLocked != null ? formatUsd(usdLocked) : '—',
    coinUnits: `${formatCryptoAmount(units)} ${gateway}`,
    rate: livePrice > 0 ? `~${formatUsd(livePrice)} (live)` : '—',
    liveUsd: formatUsd(liveUsd),
    note: 'Legacy row — verify units or rebook',
  };
};

/** Admin table: withdrawal row with coin units + USD */
export const formatAdminWithdrawalDetails = (withdrawal, coinRates = []) => {
  const coin = withdrawal?.coin_name || withdrawal?.source;
  const units = parseFloat(withdrawal?.amount) || 0;
  const isCrypto = coin && !isFiatGateway(coin);

  const usdLocked =
    withdrawal?.amount_usd != null && withdrawal?.amount_usd !== ''
      ? parseFloat(withdrawal.amount_usd)
      : null;
  const bookedRate =
    withdrawal?.rate_usd_at_booking != null && withdrawal?.rate_usd_at_booking !== ''
      ? parseFloat(withdrawal.rate_usd_at_booking)
      : null;
  const livePrice = isCrypto ? getPriceUsd(coin, coinRates) : 0;

  if (!isCrypto) {
    return {
      usd: formatUsd(units),
      coinUnits: '—',
      rate: '—',
      liveUsd: '—',
      coin: coin || '—',
    };
  }

  if (usdLocked != null && bookedRate != null && bookedRate > 0) {
    return {
      usd: formatUsd(usdLocked),
      coinUnits: `${formatCryptoAmount(units)} ${coin}`,
      rate: `${formatUsd(bookedRate)} / ${coin}`,
      liveUsd: formatUsd(units * livePrice),
      coin,
    };
  }

  const usdEst = livePrice > 0 ? units * livePrice : units;
  return {
    usd: usdLocked != null ? formatUsd(usdLocked) : formatUsd(usdEst),
    coinUnits: `${formatCryptoAmount(units)} ${coin}`,
    rate: bookedRate > 0 ? `${formatUsd(bookedRate)} / ${coin}` : livePrice > 0 ? `~${formatUsd(livePrice)} (live)` : '—',
    liveUsd: formatUsd(usdEst),
    coin,
    note: 'Legacy row — amounts may be estimated from live rate',
  };
};

/** Human-readable deposit line for fundings list */
export const formatFundingDeposit = (funding, coinRates = []) => {
  if (!funding) return '';
  const gateway = funding.gateway;
  const units = parseFloat(funding.amount) || 0;
  const usdLocked = funding.amount_usd != null ? parseFloat(funding.amount_usd) : null;

  if (isCryptoSymbol(gateway, coinRates) || (!isFiatGateway(gateway) && units > 0)) {
    const liveUsd = getCoinUsd(gateway, units, coinRates);
    if (usdLocked != null) {
      return `${units.toFixed(6)} ${gateway} ($${usdLocked.toLocaleString()} deposited, ≈ ${formatUsd(liveUsd)} now)`;
    }
    return `${units.toFixed(6)} ${gateway} (≈ ${formatUsd(liveUsd)})`;
  }

  return formatUsd(units);
};

export const formatCryptoAmount = (amount, decimals = 8) => {
  const n = parseFloat(amount) || 0;
  if (n === 0) return '0';
  return n.toFixed(decimals).replace(/\.?0+$/, '');
};
